import { forwardRef } from "react";
import { noop } from "lodash";
import { chatRoomPath } from "@circle-react/helpers/urlHelpers";
import { useChatRoomContext } from "@circle-react/hooks/chatsV2/useChatRoomContext";
import { showDateChangeLine, showUnreadMessageLine } from "../helpers";
import { MessageActions } from "./MessageActions";
import { MessageBlock } from "./MessageBlock";
import { MessageIndicatorLines } from "./MessageIndicatorLines";
import { MessageItemWrapper } from "./MessageItemWrapper";
import { RepliesBlock } from "./RepliesBlock";
import { useMessageItem } from "./hooks";

export interface MessageItemProps {
  activeMessageId: string | null;
  calculateScrollInfo: ({ event }: { event: string }) => void;
  chatRoom: any;
  chatThreadsEnabled?: boolean;
  currentParticipant: any;
  disableUnreadMessageLine?: boolean;
  displayDayChange?: boolean;
  isInitialPage?: boolean;
  isLastMessage?: boolean;
  isLiveStream?: boolean;
  isParentMessage?: boolean;
  isPinned?: boolean;
  isPinnedMessageAtTop?: boolean;
  isViewOnlyMasquerading: boolean;
  message: any;
  messageBodyClassName?: string;
  messageHashId?: string;
  onBookmarkToggle?: (messageId: any) => void;
  participants: any[];
  roles?: any;
  setActiveMessageId: (messageId: string | null) => void;
  setScrollInfo: (scrollInfo: any) => void;
  setUnreadMessageFlow?: (value: boolean) => void;
  unreadMessageId?: number | null;
}

export const MessageItem = forwardRef<any, MessageItemProps>(
  (
    {
      chatRoom,
      chatThreadsEnabled = false,
      currentParticipant,
      disableUnreadMessageLine = false,
      displayDayChange = false,
      isInitialPage = false,
      isLastMessage = false,
      isParentMessage = false,
      isPinned = false,
      isPinnedMessageAtTop = false,
      message,
      messageBodyClassName,
      messageHashId,
      onBookmarkToggle,
      participants,
      roles = {},
      setUnreadMessageFlow,
      unreadMessageId,
      isLiveStream,
      activeMessageId,
      setActiveMessageId,
      setScrollInfo,
      calculateScrollInfo,
      isViewOnlyMasquerading,
    },
    prevDayRef,
  ) => {
    const {
      canUserModifyMessage,
      day,
      handleSubmit,
      id,
      isDayChange,
      isEditMessage,
      setEditMessageId,
      shouldShowMessageActions,
      shouldShowRepliesBlock,
      toggleDeleteConfirmationModal,
    } = useMessageItem({
      chatRoom,
      chatThreadsEnabled,
      currentParticipant,
      isLastMessage,
      message,
      participants,
      prevDayRef,
      setScrollInfo,
      calculateScrollInfo,
      isViewOnlyMasquerading,
    });

    const { isMessaging } = useChatRoomContext();
    const chatRoomUrl = isMessaging ? chatRoomPath(chatRoom) : "";
    const shouldScrollIntoView =
      isInitialPage && !(messageHashId || unreadMessageId);

    return (
      <>
        <MessageIndicatorLines
          showDateChangeLine={showDateChangeLine(displayDayChange, isDayChange)}
          showUnreadMessageLine={showUnreadMessageLine({
            id,
            isPinned,
            disableUnreadMessageLine,
            unreadMessageId,
          })}
          day={day}
        />
        <MessageItemWrapper
          isPinnedMessageAtTop={isPinnedMessageAtTop}
          id={id}
          shouldShowMessageActions={shouldShowMessageActions}
          isEditMessage={isEditMessage}
          isPinned={isPinned}
          messageHashId={messageHashId}
          shouldShowRepliesBlock={shouldShowRepliesBlock}
          message={message}
          activeMessageId={activeMessageId}
        >
          <MessageBlock
            isModal={false}
            handleSubmit={handleSubmit}
            currentParticipant={currentParticipant}
            message={message}
            isEditMessage={isEditMessage}
            toggleDeleteConfirmationModal={
              isViewOnlyMasquerading ? noop : toggleDeleteConfirmationModal
            }
            isPinned={isPinned}
            roles={roles}
            isParentMessage={isParentMessage}
            chatRoom={chatRoom}
            isPinnedMessageAtTop={isPinnedMessageAtTop}
            participants={participants}
            messageBodyClassName={messageBodyClassName}
            chatRoomUrl={chatRoomUrl}
            isViewOnlyMasquerading={isViewOnlyMasquerading}
            shouldScrollIntoView={shouldScrollIntoView}
          />
          <MessageActions
            isPinned={isPinned}
            setEditMessageId={setEditMessageId}
            chatRoom={chatRoom}
            toggleDeleteConfirmationModal={
              isViewOnlyMasquerading ? noop : toggleDeleteConfirmationModal
            }
            message={message}
            currentParticipant={currentParticipant}
            canUserModifyMessage={canUserModifyMessage}
            chatThreadsEnabled={chatThreadsEnabled}
            isParentMessage={isParentMessage}
            isPinnedMessageAtTop={isPinnedMessageAtTop}
            onBookmarkToggle={onBookmarkToggle}
            setUnreadMessageFlow={setUnreadMessageFlow}
            isLiveStream={isLiveStream}
            chatRoomUrl={chatRoomUrl}
            activeMessageId={activeMessageId}
            setActiveMessageId={setActiveMessageId}
          />
          {shouldShowRepliesBlock && (
            <RepliesBlock message={message} chatRoom={chatRoom} />
          )}
        </MessageItemWrapper>
      </>
    );
  },
);

MessageItem.displayName = "MessageItem";
